import { computeCascadeOffset } from "~/components/popout/computeCascadeOffset";
import { getPopoutWidth } from "~/components/popout/constants";
import { getContentKey, type PopoutContent, type PopoutSlot, type PopoutState } from "~/contexts/PopoutManagerContext";

export interface ReducerEnv {
  now: () => number;
  createId: () => string;
  viewport: { width: number };
  maxPopouts: number;
}

export type PopoutAction =
  | { type: "OPEN"; content: PopoutContent; replace?: boolean }
  | { type: "OPEN_MANY"; contents: PopoutContent[] }
  | { type: "CLOSE"; popoutId: string }
  | { type: "MINIMIZE"; popoutId: string }
  | { type: "MINIMIZE_ALL" }
  | { type: "RESTORE"; popoutId: string }
  | { type: "RAISE"; popoutId: string }
  | { type: "CLEAR" };

const maxZIndex = (popouts: PopoutSlot[]): number => popouts.reduce((max, p) => (p.zIndex > max ? p.zIndex : max), 0);

const openSlots = (popouts: PopoutSlot[]) => popouts.filter((p) => p.status === "open");

const lruPreferMinimized = (popouts: PopoutSlot[]): PopoutSlot | null => {
  const minimized = popouts.filter((p) => p.status === "minimized");
  const candidates = minimized.length > 0 ? minimized : popouts;
  return candidates.reduce<PopoutSlot | null>((acc, p) => {
    if (acc === null) {
      return p;
    }
    return p.lastInteractedAt < acc.lastInteractedAt ? p : acc;
  }, null);
};

const evictHardCap = (popouts: PopoutSlot[], maxPopouts: number): PopoutSlot[] => {
  let next = popouts;
  while (next.length > maxPopouts) {
    const lru = lruPreferMinimized(next);
    if (lru === null) {
      break;
    }
    next = next.filter((p) => p.popoutId !== lru.popoutId);
  }
  return next;
};

/**
 * Reassigns each open slot's `initialOffset` so the row stays flush against the right edge
 * after a close or fit-eviction. Minimized slots are pills and don't compete for cascade
 * space — their offsets are left alone. Slots whose offset is unchanged are returned by
 * reference so the React key trick at the provider doesn't force needless re-mounts.
 */
const recomputeCascadeOffsets = (popouts: PopoutSlot[], viewport: { width: number }): PopoutSlot[] => {
  const openWidths: number[] = [];
  return popouts.map((p) => {
    if (p.status !== "open") {
      return p;
    }
    const offset = computeCascadeOffset(openWidths, getPopoutWidth(p.content), viewport);
    openWidths.push(getPopoutWidth(p.content));
    if (p.initialOffset.x === offset.x && p.initialOffset.y === offset.y) {
      return p;
    }
    return { ...p, initialOffset: offset };
  });
};

// Popouts render at POPOUT_Z_INDEX_BASE + zIndex and must stay below the z-50 floating tier,
// so zIndex is kept as a 1..n stacking rank. OPEN_MANY skips the hard cap, so past 9 popouts the top still reaches z-50.
const rankByZIndex = (popouts: PopoutSlot[]): PopoutSlot[] => {
  const ranks = new Map(
    popouts.toSorted((a, b) => a.zIndex - b.zIndex).map((p, rank) => [p.popoutId, rank + 1] as const),
  );
  return popouts.map((p) => {
    const zIndex = ranks.get(p.popoutId) ?? p.zIndex;
    return zIndex === p.zIndex ? p : { ...p, zIndex };
  });
};

const applyPopoutAction = (state: PopoutState, action: PopoutAction, env: ReducerEnv): PopoutState => {
  switch (action.type) {
    case "OPEN": {
      const now = env.now();
      const key = getContentKey(action.content);
      const existingIndex = state.popouts.findIndex((p) => getContentKey(p.content) === key);
      if (existingIndex !== -1) {
        const nextZ = maxZIndex(state.popouts) + 1;
        const reopen = (p: PopoutSlot): PopoutSlot => ({
          ...p,
          status: "open" as const,
          zIndex: nextZ,
          lastInteractedAt: now,
        });
        // Mobile shows one popout at a time; reopening in place (not a new slot) preserves the mounted subtree.
        if (action.replace) {
          return { popouts: state.popouts.filter((_, i) => i === existingIndex).map(reopen) };
        }
        return { popouts: state.popouts.map((p, i) => (i === existingIndex ? reopen(p) : p)) };
      }
      const newWidth = getPopoutWidth(action.content);
      const prevOpenWidths = openSlots(state.popouts).map((p) => getPopoutWidth(p.content));
      const slot: PopoutSlot = {
        popoutId: env.createId(),
        content: action.content,
        status: "open",
        initialOffset: computeCascadeOffset(prevOpenWidths, newWidth, env.viewport),
        zIndex: maxZIndex(state.popouts) + 1,
        lastInteractedAt: now,
      };
      if (action.replace) {
        return { popouts: [slot] };
      }
      const popouts = evictHardCap([...state.popouts, slot], env.maxPopouts);
      return { popouts };
    }
    case "OPEN_MANY": {
      if (action.contents.length === 0) {
        return state;
      }
      // Skips the hard cap on purpose: "open all" must surface every item, overlapping if needed.
      let popouts = state.popouts;
      let nextZ = maxZIndex(popouts);
      for (const content of action.contents) {
        const now = env.now();
        nextZ += 1;
        const key = getContentKey(content);
        const existingIndex = popouts.findIndex((p) => getContentKey(p.content) === key);
        if (existingIndex !== -1) {
          popouts = popouts.map((p, i) =>
            i === existingIndex ? { ...p, status: "open" as const, zIndex: nextZ, lastInteractedAt: now } : p,
          );
          continue;
        }
        popouts = [
          ...popouts,
          {
            popoutId: env.createId(),
            content,
            status: "open" as const,
            initialOffset: { x: 0, y: 0 },
            zIndex: nextZ,
            lastInteractedAt: now,
          },
        ];
      }
      return { popouts: recomputeCascadeOffsets(popouts, env.viewport) };
    }
    case "CLOSE": {
      return {
        popouts: recomputeCascadeOffsets(
          state.popouts.filter((p) => p.popoutId !== action.popoutId),
          env.viewport,
        ),
      };
    }
    case "MINIMIZE": {
      const popouts = state.popouts.map((p) =>
        p.popoutId === action.popoutId ? { ...p, status: "minimized" as const } : p,
      );
      return { popouts };
    }
    case "MINIMIZE_ALL": {
      if (!state.popouts.some((p) => p.status === "open")) {
        return state;
      }
      const popouts = state.popouts.map((p) => (p.status === "open" ? { ...p, status: "minimized" as const } : p));
      return { popouts };
    }
    case "RESTORE": {
      const now = env.now();
      const nextZ = maxZIndex(state.popouts) + 1;
      const popouts = state.popouts.map((p) =>
        p.popoutId === action.popoutId ? { ...p, status: "open" as const, zIndex: nextZ, lastInteractedAt: now } : p,
      );
      return { popouts };
    }
    case "RAISE": {
      const target = state.popouts.find((p) => p.popoutId === action.popoutId);
      if (!target) {
        return state;
      }
      const currentMax = maxZIndex(state.popouts);
      // No-op when already on top: every mousedown/focus inside the popout fires
      // raisePopout, and an unnecessary state update re-renders the subtree mid-
      // click, which breaks libraries that focus on click (e.g. react-day-picker
      // in the custom snooze modal). Skip the rerender entirely when nothing changes.
      if (target.zIndex === currentMax) {
        return state;
      }
      const now = env.now();
      const popouts = state.popouts.map((p) =>
        p.popoutId === action.popoutId ? { ...p, zIndex: currentMax + 1, lastInteractedAt: now } : p,
      );
      return { popouts };
    }
    case "CLEAR": {
      if (state.popouts.length === 0) {
        return state;
      }
      return { popouts: [] };
    }
  }
};

export const popoutManagerReducer = (state: PopoutState, action: PopoutAction, env: ReducerEnv): PopoutState => {
  const next = applyPopoutAction(state, action, env);
  return next === state ? state : { ...next, popouts: rankByZIndex(next.popouts) };
};

export const initialPopoutState: PopoutState = { popouts: [] };
